'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { getAuthToken, clearAllStorageAndCache } from '@/lib/auth'
import { setPostLoginRedirect } from '@/lib/postLoginRedirect'
import { isEnterpriseLoginEnabled } from '@/lib/enterpriseLoginFeature'
import { isMockInterviewPath } from '@/lib/mockInterviewEntry'
import { isJdInterviewStartPath } from '@/lib/jdInterviewEntry'
import { useLoginModal } from '@/context/LoginModalContext'

function redirectToSignin(pathname: string) {
  if (typeof window !== 'undefined') {
    const fullPath = window.location.pathname + window.location.search
    if (pathname.startsWith('/interview')) {
      setPostLoginRedirect(pathname + window.location.search)
    } else if (isMockInterviewPath(pathname) || isJdInterviewStartPath(pathname)) {
      setPostLoginRedirect(fullPath)
    }
  }
  window.location.replace('/')
}

/**
 * On protected routes: if no token exists, clear storage and redirect to signin.
 * Does NOT call current-user on every route/refresh; token validity is enforced
 * by the backend on the first API call (401 → global handler in authenticatedFetch).
 */
export default function TokenValidator() {
  const pathname = usePathname()
  const { openLoginModal } = useLoginModal()

  useEffect(() => {
    const path = (typeof window !== 'undefined' ? window.location.pathname : null) ?? pathname
    const publicPaths = [
      '/signin',
      '/login',
      '/',
      '/enterprise',
      '/enterprise/terms',
      '/enterprise/privacy',
      '/privacy',
      '/terms',
      '/contact',
      '/about',
      // Standalone free mock interview campaign page — no auth required to view.
      '/free-15-minute-mock-interview',
      // Dussehra festival campaign landing — direct URL, no auth required to view.
      '/dussehra-offer',
      // Interview-practice campaign landers — shared layout, direct URL, no auth to view.
      // Every new campaign route must be listed here or TokenValidator treats it as protected and
      // bounces the visitor to /signin (which lands them on '/').
      '/data-analytics-engineering',
      '/cloud-devops-platform-engineering',
      '/security-engineering',
      '/mlops-forward-deployed-engineering',
      // QR landing page reached from printed visiting cards — scanned by people who have
      // never signed in, so it must never bounce to '/' (or wipe their storage on the way).
      '/overview',
      // Internal Puppeteer invoice rendering — no user session available in headless context.
      '/invoice-preview',
      // Legacy course email link: redirects to /mock-interview (claim runs there after login).
      '/courses/claim',
      // Course partner reward link (API interview_url): same redirect + postLoginRedirect as claim.
      '/reward/interview',
      // Spec recruiter invite entry (/invite?token=... server-redirects to auto-login).
      '/invite',
      ...(isEnterpriseLoginEnabled()
        ? (['/enterprise-auth', '/enterprise/'] as const)
        : []),
    ]
    if (publicPaths.includes(path || '') || (path && path.startsWith('/auth/'))) {
      return
    }
    // Recruiter invite magic link: no local session until POST exchange; must not send to /signin (which redirects to / and drops ?token=).
    if (path && path.startsWith('/enterprise/auto-login')) {
      return
    }
    // B2B checkout checks the account type itself. A no-token redirect to "/" here would
    // race that gate and drop the retry URL (return_to) before B2B login can resume it.
    if (path && path.startsWith('/enterprise/checkout')) {
      return
    }
    // Scheduled interview email links: /interview/<token> validates without prior login; JWT is minted on that page.
    if (path && path.startsWith('/interview/')) {
      return
    }
    // Mock interview / course-reward deep link: same LoginModal as landing; do not send to /signin.
    if (path && isMockInterviewPath(path)) {
      return
    }
    // JD interview email/deep links: /jd-interview/start opens the LoginModal in place and
    // postLoginRedirect keeps ?jd_id=..., so do not bounce through /signin.
    if (path && isJdInterviewStartPath(path)) {
      return
    }

    const token = getAuthToken()
    if (!token) {
      const fullPath = typeof window !== 'undefined'
        ? window.location.pathname + window.location.search
        : pathname
      if (path === '/checkout') {
        // B2C: open login modal in-place; postLoginRedirect survives the storage wipe.
        clearAllStorageAndCache().then(() => {
          setPostLoginRedirect(fullPath)
          openLoginModal(undefined, undefined, { required: true })
        })
      } else {
        clearAllStorageAndCache().then(() => redirectToSignin(pathname))
      }
    }
  }, [pathname, openLoginModal])

  return null
}

